// Where the visitor is, from the deployed site's `GET /_country`
// (CloudFront, the sibling piece in `infra/`): `{"country":"ES"}`, ISO
// 3166-1 alpha-2 in upper case, or `{"country":""}` when CloudFront cannot
// place the viewer. On the dev server the path does not exist and the
// answer is the app's HTML (or a 404): unknown, like any failure, a
// timeout or a bad body. The answer is kept for the session in
// `sessionStorage` so a reload does not ask again; a failure is not kept,
// so the next load tries once more. Storage may throw (private mode, a
// blocked origin): every touch of it is wrapped.

export const COUNTRY_PATH = '/_country';
export const COUNTRY_KEY = 'cc.country';

/** The bit of web storage the lookup needs; `sessionStorage` in the browser, a stub in tests. */
export interface CountryCache {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The country in the endpoint's body: a two-letter upper-case code, '' when it could not place the viewer, undefined for anything else (HTML, a wrong shape). */
export function parseCountry(text: string): string | undefined {
  try {
    const value: unknown = JSON.parse(text);
    if (typeof value !== 'object' || value === null) return undefined;
    const { country } = value as Record<string, unknown>;
    if (typeof country !== 'string') return undefined;
    const code = country.trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code) ? code : '';
  } catch {
    return undefined;
  }
}

function sessionCache(): CountryCache | undefined {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * The visitor's country, '' when the site could not tell, undefined when
 * the lookup failed (no endpoint, a non-200, a body that is not its JSON,
 * the timeout). Same-origin, never cached by the browser, answered from the
 * session's cache when there is one.
 */
export async function fetchCountry(fetchFn: typeof fetch = fetch, timeoutMs = 1500, cache: CountryCache | undefined = sessionCache()): Promise<string | undefined> {
  try {
    const kept = cache?.getItem(COUNTRY_KEY);
    if (typeof kept === 'string') return kept;
  } catch {
    // storage unavailable: look it up
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let country: string | undefined;
  try {
    const response = await fetchFn(COUNTRY_PATH, { cache: 'no-store', signal: controller.signal, headers: { accept: 'application/json' } });
    if (response.status !== 200) return undefined;
    country = parseCountry(await response.text());
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
  if (country === undefined) return undefined;
  try {
    cache?.setItem(COUNTRY_KEY, country);
  } catch {
    // storage unavailable: the next load asks again
  }
  return country;
}
